export interface ManualTable {
  columns: string[]
  rows: string[][]
}

export interface ManualFlowItem {
  step: string
  title: string
  page: string
  action: string
  result: string
}

export interface ManualExampleStep {
  title: string
  page: string
  action: string
  expected: string
}

export interface ManualExample {
  title: string
  scenario: string
  input: string
  target: string
  steps: ManualExampleStep[]
}

export interface ManualSection {
  id: string
  title: string
  summary?: string
  flow?: ManualFlowItem[]
  example?: ManualExample
  steps?: string[]
  checklist?: string[]
  table?: ManualTable
  tips?: string[]
  warning?: string
}

export interface ManualStatusItem {
  label: string
  value: string
  tone?: string
}

export interface ManualHighlight {
  label: string
  value: string
}

export interface ManualContent {
  title: string
  subtitle: string
  audience: string
  updatedAt: string
  highlights: ManualHighlight[]
  statusLegend: ManualStatusItem[]
  sections: ManualSection[]
}

export const receptionManual: ManualContent = {
  title: '售后接待手册',
  subtitle: '围绕消息接住、草稿审核、建单跟踪、客户确认和 SLA 处置的日常操作指南。',
  audience: '接待人、协助接待、需要理解接待主链的管理员',
  updatedAt: '2026-06-05',
  highlights: [
    { label: '主入口', value: '工作台、消息中心、工单草稿、工单跟踪、SLA' },
    { label: '关键权限', value: 'message:read / draft:update / ticket:customer_confirm' },
    { label: '闭环口径', value: '技术完成不等于关闭，客户确认后才闭环' },
  ],
  statusLegend: [
    { label: '消息待响应', value: '先在客户群确认收到，再在平台标记响应', tone: 'warning' },
    { label: '草稿待审核', value: '核对事实、优先级、初始指派研发和缺失信息', tone: 'processing' },
    { label: '待客户确认', value: '复制话术同步客户，并记录通过或退回', tone: 'success' },
    { label: 'SLA 待处理', value: '查看详情后提醒、忽略或标记解决', tone: 'error' },
  ],
  sections: [
    {
      id: 'ticket-flow-map',
      title: '1. 完整建单处理流程图',
      summary: '下面这张图按一条客户问题从进入平台到客户确认闭环的顺序组织。日常处理时按从左到右的顺序推进，任何一步卡住都先回到对应页面排查。',
      flow: [
        {
          step: '01',
          title: '客户消息进入',
          page: '工作台 / 消息中心',
          action: '查看待响应消息，确认群、客户、发送人和摘要。',
          result: '消息处于待响应或已入库状态。',
        },
        {
          step: '02',
          title: '首响接住',
          page: '客户微信群 + 消息中心',
          action: '先在客户群回复“已收到，正在确认”，再点击标记响应。',
          result: '消息进入已响应，首响时间被记录。',
        },
        {
          step: '03',
          title: '生成草稿',
          page: '消息中心',
          action: '对需要处理的问题点击生成草稿。',
          result: '页面跳转到草稿审核页。',
        },
        {
          step: '04',
          title: '审核事实',
          page: '工单草稿详情',
          action: '核对来源消息、绑定信息、AI 判断、标题、优先级和缺失信息。',
          result: '草稿内容变成可执行工单描述。',
        },
        {
          step: '05',
          title: '确认草稿',
          page: '工单草稿详情',
          action: '选择有效初始指派研发和客户确认跟进人后保存并确认草稿。',
          result: '草稿进入已确认，可继续建单。',
        },
        {
          step: '06',
          title: '创建禅道工单',
          page: '工单草稿详情',
          action: '在创建弹窗中复核执行、初始指派研发和优先级，确认创建。',
          result: '生成禅道 task 和平台工单映射。',
        },
        {
          step: '07',
          title: '跟踪处理',
          page: '工单跟踪详情',
          action: '关注待接单、处理中、待客户确认和 SLA 风险。',
          result: '平台状态随禅道 task 同步推进。',
        },
        {
          step: '08',
          title: '客户确认闭环',
          page: '工单跟踪详情 + 客户群',
          action: '复制话术同步客户，按客户反馈选择确认通过或退回。',
          result: '通过后关闭；退回后继续处理。',
        },
      ],
      checklist: [
        '首响和客户确认必须先发生在客户沟通侧，再回平台记录。',
        'AI 草稿只是建议，确认草稿前必须人工核对事实。',
        '创建禅道工单成功后才算进入技术处理链，草稿本身不是工单。',
        '客户确认通过后才算闭环，技术完成不等于关闭。',
      ],
    },
    {
      id: 'ticket-flow-example',
      title: '2. 完整建单处理实例',
      summary: '以下示例用一条客户群消息演示完整操作。可以把它当成培训时的标准演练脚本。',
      example: {
        title: '实例：景区游客中心大屏黑屏',
        scenario: '客户在“西湖景区售后群”反馈：游客中心 2 号大屏黑屏，现场重启盒子后仍无画面，影响当天讲解接待。',
        input: '客户原文：@售后 游客中心2号大屏黑屏，盒子重启过还是不亮，今天上午有接待，麻烦尽快看一下。附件：现场照片 1 张。',
        target: '目标：接待人在平台完成首响记录、生成并审核草稿、创建禅道 task，跟踪技术处理，最后记录客户确认或退回。',
        steps: [
          {
            title: '查看待响应消息',
            page: '工作台 -> 待响应消息，或消息中心按“待响应”筛选',
            action: '打开消息详情，确认群名是西湖景区售后群，客户已绑定，附件能看到或已加入下载队列。',
            expected: '确认这不是简单咨询，应该进入工单草稿。',
          },
          {
            title: '完成首响',
            page: '客户微信群 + 消息中心',
            action: '先在群里回复“收到，我们先记录工单并安排技术确认大屏和盒子状态”。然后在消息中心点击标记响应。',
            expected: '消息状态从待响应变为已响应，避免首响 SLA 继续计时。',
          },
          {
            title: '生成草稿',
            page: '消息中心',
            action: '点击生成草稿，等待生成成功后进入草稿审核页。',
            expected: '草稿标题类似“游客中心 2 号大屏黑屏需排查”，状态为待审核。',
          },
          {
            title: '核对草稿事实',
            page: '工单草稿详情',
            action: '检查来源消息、附件、绑定客户、平台项目、禅道产品/项目；把优先级设为 P2，问题类型选现场故障。',
            expected: '已知事实包含“大屏黑屏、已重启盒子、当天上午有接待”；待补信息如缺设备编号，可准备追问。',
          },
          {
            title: '补齐责任人和研发',
            page: '工单草稿详情 -> 初始指派候选',
            action: '选择有效禅道研发账号；客户确认跟进人填写当前接待或项目确认人。',
            expected: '创建禅道工单按钮不再因无有效研发而禁用。',
          },
          {
            title: '确认并创建工单',
            page: '工单草稿详情',
            action: '点击确认草稿；确认后点击创建禅道工单，在弹窗中复核执行、研发、优先级和摘要。',
            expected: '创建成功后跳转工单详情，能看到禅道 task ID 和平台状态待接单。',
          },
          {
            title: '跟踪技术处理',
            page: '工单跟踪详情',
            action: '关注状态链。如果长时间待接单或处理中无进展，进入 SLA 页面提醒责任人。',
            expected: '技术在禅道开始处理、补充过程并完成后，平台同步到待客户确认。',
          },
          {
            title: '同步客户确认',
            page: '工单跟踪详情 + 客户微信群',
            action: '待客户确认时复制页面话术到客户群。客户确认画面恢复后，点击确认通过并关闭，填写“客户确认大屏恢复显示”。',
            expected: '工单状态进入已关闭；若客户反馈仍黑屏，则点击客户退回继续处理并填写退回原因。',
          },
        ],
      },
      tips: [
        '如果第 4 步发现群未绑定，不要继续建单；先请管理员在群映射中补齐客户、项目和负责人。',
        '如果第 5 步找不到研发，先请管理员同步禅道账号或修正用户外部账号。',
      ],
    },
    {
      id: 'role-scope',
      title: '3. 角色边界',
      summary: '接待人的职责是把客户问题接住、澄清、转成可执行工单，并把处理结果带回客户侧。',
      table: {
        columns: ['负责事项', '不负责事项', '判断标准'],
        rows: [
          ['首响、追问、草稿确认、客户确认', '系统账号、SLA 规则、全局群映射配置', '是否直接影响本次客户问题闭环'],
          ['人工录入消息、生成草稿、拒绝无需建单事项', '替技术人员在禅道完成研发动作', '是否需要接待人与客户沟通确认'],
          ['关注自己负责范围内的 SLA 和退回事项', '替管理员处理任务失败和运行环境问题', '是否属于当前群或当前工单责任链'],
        ],
      },
      tips: [
        '页面按钮由当前账号权限和后端 operator_actions 控制。看不到按钮时，先确认账号角色和群范围。',
        '客户群里的口头沟通不会自动改变平台状态，关键节点必须在操作台显式记录。',
      ],
    },
    {
      id: 'daily-order',
      title: '4. 每日处理顺序',
      summary: '先看待办，再处理列表，最后复核异常，避免在多个页面之间来回跳转。',
      steps: [
        '进入工作台，查看今日新消息、待响应、待确认草稿、待客户确认、响应超时和失败任务摘要。',
        '打开消息中心，优先处理待响应和待补绑定消息。',
        '打开工单草稿，审核 drafted、need_more_info、binding_required 状态的草稿。',
        '打开工单跟踪，处理 waiting_customer_confirm 和 returned 状态的工单。',
        '打开响应时限提醒，处理 open 或 reminded 的 SLA 事件。',
        '必要时检索知识条目，复用已有结论和处理口径。',
      ],
      checklist: [
        '待响应消息是否都已在客户群首响。',
        '待审核草稿是否都有明确标题、优先级、初始指派研发和客户确认跟进人。',
        '待客户确认工单是否已同步客户并记录结论。',
        '超时事项是否已经提醒、解决或填写忽略原因。',
      ],
    },
    {
      id: 'dashboard',
      title: '5. 工作台怎么看',
      summary: '工作台是接待人的今日待办入口，不替代详情页。',
      table: {
        columns: ['区块', '重点字段', '下一步'],
        rows: [
          ['待响应消息', '时间、客户、摘要、消息状态', '进入消息中心查看详情、标记响应或生成草稿'],
          ['待确认草稿', '草稿号、客户、标题、优先级、风险', '进入草稿审核页继续审核'],
          ['响应时限提醒', '规则、工单、责任人、命中时间', '进入 SLA 页面处理事件'],
          ['最近失败任务', '任务码、错误信息、开始时间', '通知管理员查看任务运行页'],
        ],
      },
      tips: [
        '工作台按钮用于跳转和快速查看；真正的写操作以消息、草稿、工单和 SLA 详情页为准。',
      ],
    },
    {
      id: 'messages',
      title: '6. 消息中心操作',
      summary: '消息中心负责把客户消息从“原始事实”推进到“已响应、无需建单或草稿”。',
      table: {
        columns: ['动作', '使用条件', '动作结果'],
        rows: [
          ['查看', '需要确认原文、附件、绑定或历史事件', '打开详情抽屉，不改变状态'],
          ['标记响应', '已在客户群确认收到或已做首响', '记录首响，消息进入已响应'],
          ['生成草稿', '消息需要形成售后工单或需要 AI 辅助结构化', '生成草稿并跳转草稿审核页'],
          ['无需建单', '简单咨询、重复消息或无需进入工单链', '消息关闭为无需建单'],
          ['人工录入', '客户问题未通过归档进入平台，且当前账号有可写群范围', '补录消息后进入消息列表'],
        ],
      },
      checklist: [
        '人工录入时必须选择群、发送人和发送时间。',
        '正文和有效附件至少保留一个。',
        '纯触发词消息只作为上下文，不应生成草稿或标记为业务事实。',
      ],
    },
    {
      id: 'drafts',
      title: '7. 草稿审核',
      summary: '草稿是 AI 建议，不是工单事实。接待人必须核对后才能确认或建单。',
      steps: [
        '先看触发来源和来源消息，确认客户问题是否完整。',
        '检查绑定信息，确认群名、客户、项目、禅道产品和项目是否正确。',
        '查看 AI 判断的置信度和风险标签，低置信度时优先补充事实。',
        '修订标题、问题类型、优先级、是否需要建单、客户确认跟进人和摘要。',
        '在研发候选池中选择有效研发账号，避免建单时无法指派。',
        '需要更多信息时点击追问信息；无需建单时点击拒绝建单并填写原因。',
        '确认无误后点击确认草稿；已确认草稿才进入创建禅道工单阶段。',
      ],
      checklist: [
        '标题是否能让研发一眼知道要处理什么。',
        '优先级是否和客户影响范围一致。',
        '待补信息是否已经转成追问口径。',
        '验收标准是否能支持客户确认。',
        '初始指派研发是否有效且可在禅道接收 task。',
      ],
      warning: '确认草稿和创建禅道工单是两个关键节点。若页面提供连续操作，也必须在失败时停留在当前页，不要把未成功建单当成已闭环。',
    },
    {
      id: 'create-ticket',
      title: '8. 创建禅道工单',
      summary: '创建工单会把平台草稿写入禅道 task，并生成平台工单映射。',
      steps: [
        '在草稿审核页确认草稿后，检查创建工单弹窗中的标题、优先级、客户确认跟进人、执行和初始指派研发。',
        '如存在多个同名执行，必须人工选择目标执行。',
        '点击创建禅道工单后等待成功提示，成功后页面跳转到工单详情。',
        '进入工单详情后确认平台状态为待接单，并记录禅道 task ID。',
      ],
      tips: [
        '默认执行只是兼容兜底；实际建单会优先按草稿内容解析或创建执行。',
        '建单失败时不要重复快速点击，应先阅读错误提示，再确认绑定、执行和研发账号。',
      ],
    },
    {
      id: 'tickets',
      title: '9. 工单跟踪与客户确认',
      summary: '工单跟踪页连接平台状态和禅道 task 状态，是客户确认闭环入口。',
      table: {
        columns: ['状态', '接待动作', '注意事项'],
        rows: [
          ['待接单', '关注当前执行人是否接单，必要时提醒或改派', '长期未接单会触发 SLA'],
          ['处理中', '查看最近动作和来源消息，必要时补充客户信息', '技术处理过程以禅道为主'],
          ['待客户确认', '复制确认话术到客户群，收集客户结论', '话术只复制，不自动发送'],
          ['已退回', '填写退回原因后推动技术继续处理', '退回原因必须可审计'],
          ['已关闭', '确认闭环完成，必要时进入知识沉淀', '客户确认通过才算关闭'],
        ],
      },
      checklist: [
        '客户确认通过时必须填写确认备注。',
        '客户退回时必须填写退回原因。',
        '手动同步用于刷新禅道状态，不替代技术在禅道里的处理动作。',
      ],
    },
    {
      id: 'sla',
      title: '10. 响应时限提醒',
      summary: 'SLA 页面用于处理已命中的超时事件，重点是让责任链继续推进。',
      table: {
        columns: ['动作', '适用场景', '结果'],
        rows: [
          ['详情', '需要确认命中规则、责任人、对象和提醒历史', '打开详情抽屉'],
          ['提醒', '责任人尚未推进，需要催办', '事件进入已提醒或增加提醒记录'],
          ['忽略', '确认不需要按本次规则处理', '必须填写忽略原因，事件进入已忽略'],
          ['解决', '超时事项已处理或对象状态已推进', '填写说明后事件进入已解决'],
        ],
      },
      warning: '不要为了清空列表随意忽略 SLA。忽略代表人工确认该提醒不再需要处理，原因会进入审计链路。',
    },
    {
      id: 'knowledge',
      title: '11. 知识条目',
      summary: '知识条目用于检索已发布的经验，不是工单状态事实源。',
      steps: [
        '在知识条目页按标题、正文关键字或关键词搜索。',
        '打开详情抽屉查看完整处理说明。',
        '复用知识时仍需结合当前客户、项目、版本和现象判断。',
      ],
      tips: [
        '接待人通常只查看已发布知识；发布和归档由 manager 或 admin 处理。',
      ],
    },
    {
      id: 'exceptions',
      title: '12. 常见异常处理',
      table: {
        columns: ['异常', '先检查', '处理方式'],
        rows: [
          ['未绑定群', '消息或草稿是否显示 binding_required', '联系管理员校准群映射后再继续生成草稿或建单'],
          ['低置信度草稿', '风险标签和待补信息', '核对来源消息，必要时修订摘要或补录上下文后人工确认；置信度不阻断确认'],
          ['找不到研发', '研发候选池和禅道账号', '联系管理员同步禅道账号或修正用户外部账号'],
          ['创建工单失败', '绑定、执行路由、初始指派研发和错误提示', '修正后重试，不要把草稿当作已建单'],
          ['客户不认可处理结果', '工单详情当前状态和确认话术', '点击客户退回继续处理并填写原因'],
        ],
      },
    },
  ],
}

export const adminManual: ManualContent = {
  title: '管理员手册',
  subtitle: '面向账号权限、群映射、SLA 配置、任务运行和知识治理的平台管理指南。',
  audience: '系统管理员、售后负责人、负责平台配置和运行治理的运维人员',
  updatedAt: '2026-06-12',
  highlights: [
    { label: '主入口', value: '用户管理、群映射、SLA 设置、任务运行、知识条目' },
    { label: '关键权限', value: 'user:manage / binding:manage / system:health_read' },
    { label: '治理口径', value: '配置动作必须可回溯，高风险动作保留原因或二次确认' },
  ],
  statusLegend: [
    { label: '用户启用', value: '可登录且按角色与范围访问数据', tone: 'success' },
    { label: '用户锁定', value: '需要管理员解锁或重置密码', tone: 'error' },
    { label: '群待验证', value: '绑定信息未完成校准，可能阻塞草稿建单', tone: 'warning' },
    { label: '任务失败', value: '需要查看错误摘要并定位依赖或数据问题', tone: 'error' },
  ],
  sections: [
    {
      id: 'admin-scope',
      title: '1. 管理员职责',
      summary: '管理员负责平台可用性和配置正确性，不建议替代接待人处理大量日常工单。',
      table: {
        columns: ['领域', '管理员动作', '边界'],
        rows: [
          ['账号权限', '新增、编辑、停用、解锁、重置密码、吊销会话', '不替用户保管长期密码'],
          ['群映射', '校准客户、项目、禅道产品/项目、接待负责人、默认首派研发和业务阶段', '不把微信群长期绑定到固定 execution'],
          ['SLA 设置', '调整阈值、提醒间隔、二级升级和抄送', '变更前应确认业务影响'],
          ['运行监控', '查看失败任务和系统异常摘要', '业务状态修复仍需回到对应详情页'],
          ['知识治理', '发布、归档知识条目', '不把未验证结论发布为知识'],
        ],
      },
    },
    {
      id: 'users',
      title: '2. 账号开通与授权',
      summary: '用户管理页是平台账号、角色和对象级范围的事实入口。',
      steps: [
        '进入用户管理，点击新增用户。',
        '填写账号、姓名、邮箱、状态和启用开关。',
        '选择角色：接待、技术、经理、管理员或审计；一个用户可以兼任多个角色。',
        '为接待和协助人员配置群范围，并为每个群选择主接待、协助接待或只读查看。',
        '仅在跨项目协作、临时协助或无固定群绑定兜底时配置高级项目/执行范围。',
        '填写或同步禅道账号，确保研发候选和建单指派能匹配。',
        '保存用户后，如需密码登录，进入该用户行操作并重置密码。',
      ],
      checklist: [
        '接待账号优先配置群范围，不要用项目范围替代常规群授权。',
        '只读查看不能执行写操作。',
        '审计角色只读，不能执行高风险动作。',
        '新增账号后应验证其菜单和数据范围是否符合预期。',
      ],
    },
    {
      id: 'login-security',
      title: '3. 登录安全维护',
      summary: '登录安全动作集中在用户管理页的行操作中。',
      table: {
        columns: ['动作', '使用场景', '结果'],
        rows: [
          ['重置密码', '新账号下发临时密码、用户忘记密码或密码泄露', '设置临时密码，可要求下次登录强制改密，并吊销既有会话'],
          ['解锁', '账号状态锁定、临时锁定或存在失败次数', '清空锁定时间和失败次数，必要时恢复启用'],
          ['吊销会话', '人员离岗、权限调整或怀疑会话泄露', '目标用户未过期登录会话立即失效'],
          ['停用账号', '人员离岗或暂不允许登录', '账号不可继续作为有效用户使用'],
        ],
      },
      warning: '重置密码、解锁和吊销会话都会影响用户登录状态，执行前应确认目标账号无误。',
    },
    {
      id: 'group-bindings',
      title: '4. 群映射校准',
      summary: '群映射决定消息路由、责任人、客户项目和后续禅道建单上下文。',
      steps: [
        '进入群映射，按群、客户、项目或执行搜索目标群。',
        '按接待账号过滤目标群；输入 unassigned 可查看未分配接待的群。',
        '需要离线核对或交接时，点击导出Excel，系统会按当前搜索、接待账号和启用状态筛选导出全部匹配群。',
        '点击校准，确认 room_id、当前禅道产品、项目和默认执行。',
        '填写群名称、客户名称和平台项目名称；项目名应是客户或业主长期容器名。',
        '选择禅道产品和禅道项目。',
        '默认执行/兼容兜底可不设置；建单阶段会按草稿内容解析或创建执行。',
        '选择接待负责人、默认首派研发、客户级别和业务阶段。',
        '填写必要备注后保存。',
      ],
      checklist: [
        'product 是项目创建前置，project 是客户长期容器，execution 是售后周期或专项容器，task 才是单条工单。',
        '不要把微信群长期绑定到固定 execution。',
        '客户级别和业务阶段会影响后续运营判断，应避免长期保持待定。',
        '导出的 Excel 包含 ID、群 ID、群名、接待、技术、客户、项目、禅道映射、启用和验证状态，适合批量核对。',
        '保存后检查列表是否显示已验证或信息完整。',
      ],
    },
    {
      id: 'zentao-sync',
      title: '5. 禅道信息同步与项目生成',
      summary: '群映射抽屉内可刷新禅道候选，也可在条件满足时生成禅道项目。',
      table: {
        columns: ['动作', '前置条件', '说明'],
        rows: [
          ['禅道信息同步', '账号具备群映射校准权限，禅道实时连接可用', '同步产品、项目、执行和研发用户候选；表单仍可继续编辑'],
          ['推荐项目名', '已填写群名、客户名或项目名', '按命名规则推荐长期项目名称'],
          ['生成禅道项目', '已选择禅道产品，当前群未绑定禅道项目', '创建项目并写回绑定；默认执行保持可选兜底'],
          ['选择默认执行', '确有兼容兜底需要', '只作为兜底，不替代建单阶段的执行路由'],
        ],
      },
      warning: '项目名称不得包含阶段、时间或“售后、维保、一期”等执行层含义；这些信息应在 execution 层处理。',
    },
    {
      id: 'sla-rules',
      title: '6. 响应时限设置',
      summary: '响应时限设置页只对管理员开放，用于维护 SLA 规则参数。',
      table: {
        columns: ['字段', '含义', '建议'],
        rows: [
          ['超时阈值', '多久未推进算命中 SLA', '结合业务阶段和客户等级谨慎调整'],
          ['提醒间隔', '重复提醒之间的最短间隔', '避免过密提醒造成噪音'],
          ['二级升级', '多轮未解决后是否升级', '重点客户或关键阶段建议启用'],
          ['升级等待', '达到二级升级前等待时间', '应大于常规提醒间隔'],
          ['二级抄送', '升级时抄送邮箱', '填写真实可达邮箱，多个地址按系统约定维护'],
          ['启用状态', '规则是否参与扫描', '停用前确认不会漏掉关键链路'],
        ],
      },
      checklist: [
        '修改后必须点击当前行保存。',
        '只改页面草稿值不会生效。',
        'SLA 规则变更应同步告知接待负责人和当前执行人。',
      ],
    },
    {
      id: 'job-runs',
      title: '7. 任务运行监控',
      summary: '任务运行页展示后台任务结果，用于定位同步、扫描、通知和触发任务异常。',
      table: {
        columns: ['字段', '怎么看', '异常处理'],
        rows: [
          ['状态', '成功、部分成功、失败、运行中', '失败和部分成功都需要查看摘要和错误信息'],
          ['任务码', '识别是 SLA、禅道同步、触发扫描还是其他任务', '按任务类型找对应运维脚本或配置'],
          ['候选/成功/失败/警告', '判断影响范围', '失败数量不为 0 时保留证据并排查'],
          ['错误信息', '定位依赖、权限、数据或网络问题', '不要直接清理记录，应先确认根因'],
        ],
      },
      tips: [
        '工作台只显示最近失败任务摘要；完整排查应进入任务运行页。',
        '任务失败不一定代表业务数据已损坏，先区分读取失败、外部依赖失败和写入失败。',
      ],
    },
    {
      id: 'knowledge-governance',
      title: '8. 知识治理',
      summary: '知识条目页支持检索、发布草稿和归档已发布条目。',
      table: {
        columns: ['动作', '适用对象', '结果'],
        rows: [
          ['检索', '所有可访问知识页的用户', '查看知识标题、关键词、来源工单和摘要'],
          ['详情', '需要复核完整内容时', '打开详情抽屉'],
          ['发布', 'manager 或 admin，且条目为草稿', '条目变为已发布，普通用户可检索'],
          ['归档', 'manager 或 admin，且条目已发布', '条目从常规搜索结果中隐藏'],
        ],
      },
      checklist: [
        '发布前确认结论已被验证。',
        '归档前确认条目过期、重复或不应继续被引用。',
      ],
    },
    {
      id: 'risk-actions',
      title: '9. 高风险动作清单',
      summary: '高风险动作必须可审计，管理员应推动团队填写原因和备注。',
      table: {
        columns: ['高风险动作', '页面', '要求'],
        rows: [
          ['重置密码、解锁、吊销会话', '用户管理', '确认账号，必要时通知用户重新登录'],
          ['生成禅道项目', '群映射', '确认产品、项目名和重复项目检查结果'],
          ['忽略 SLA', '响应时限提醒', '填写原因，避免掩盖真实超时'],
          ['客户确认关闭或退回', '工单详情', '填写确认备注或退回原因'],
          ['发布知识', '知识条目', '确认内容已复核，避免错误经验扩散'],
        ],
      },
      warning: '管理员可以执行更多动作，但不应绕过业务责任链。配置修复后，仍要让对应接待负责人或当前执行人在业务页面完成闭环。',
    },
    {
      id: 'admin-troubleshooting',
      title: '10. 常见治理问题',
      table: {
        columns: ['问题', '排查入口', '处理方式'],
        rows: [
          ['用户看不到菜单', '用户管理、顶栏角色标签', '确认角色和权限，必要时重新登录或吊销会话后再登录'],
          ['用户看不到负责群数据', '用户管理的群范围、群映射 room_id', '补充群范围或修正绑定 room_id'],
          ['草稿无法建单', '草稿详情、群映射、禅道账号', '确认绑定、执行路由和初始指派研发有效'],
          ['SLA 大量误报', '响应时限设置、SLA 事件列表', '检查阈值、提醒间隔和业务阶段口径'],
          ['任务连续失败', '任务运行、工作台失败摘要、运维脚本日志', '保留 run_id 和错误信息，按任务码定位外部依赖或数据问题'],
        ],
      },
    },
  ],
}
